export type ReconciliationTimerHandle = ReturnType<typeof setTimeout>;

export interface ReconciliationTimerApi {
  setTimeout(callback: () => void, delayMs: number): ReconciliationTimerHandle;
  clearTimeout(handle: ReconciliationTimerHandle): void;
}

export interface TaskReconciliationSchedulerOptions {
  delayMs?: number;
  minIntervalMs?: number;
  jitterMs?: number;
  timerApi?: ReconciliationTimerApi;
  now?: () => number;
  random?: () => number;
}

const defaultTimerApi: ReconciliationTimerApi = {
  setTimeout: (callback, delayMs) => setTimeout(callback, delayMs),
  clearTimeout: (handle) => clearTimeout(handle),
};

export class TaskReconciliationScheduler {
  private timer: ReconciliationTimerHandle | null = null;
  private readonly reasons = new Set<string>();
  private isFlushing = false;
  private lastFlushStartedAt: number | null = null;
  private readonly delayMs: number;
  private readonly minIntervalMs: number;
  private readonly jitterMs: number;
  private readonly timerApi: ReconciliationTimerApi;
  private readonly now: () => number;
  private readonly random: () => number;

  constructor(
    private readonly onFlush: (reasons: string[]) => void | Promise<void>,
    options: TaskReconciliationSchedulerOptions = {},
  ) {
    this.delayMs = options.delayMs ?? 250;
    this.minIntervalMs = options.minIntervalMs ?? 2_000;
    this.jitterMs = options.jitterMs ?? 750;
    this.timerApi = options.timerApi ?? defaultTimerApi;
    this.now = options.now ?? Date.now;
    this.random = options.random ?? Math.random;
  }

  schedule(...reasons: string[]): void {
    reasons.filter(Boolean).forEach((reason) => this.reasons.add(reason));

    if (this.timer !== null || this.isFlushing) {
      return;
    }

    this.scheduleFlush();
  }

  dispose(): void {
    if (this.timer !== null) {
      this.timerApi.clearTimeout(this.timer);
      this.timer = null;
    }
    this.reasons.clear();
  }

  private scheduleFlush(): void {
    if (this.timer !== null || this.isFlushing || this.reasons.size === 0) {
      return;
    }

    const now = this.now();
    const jitter = Math.floor(this.random() * (this.jitterMs + 1));
    const coalescingDueAt = now + this.delayMs + jitter;
    const rateLimitDueAt = this.lastFlushStartedAt === null
      ? now
      : this.lastFlushStartedAt + this.minIntervalMs;
    const delay = Math.max(0, Math.max(coalescingDueAt, rateLimitDueAt) - now);

    this.timer = this.timerApi.setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, delay);
  }

  private async flush(): Promise<void> {
    if (this.isFlushing || this.reasons.size === 0) {
      return;
    }

    const pendingReasons = Array.from(this.reasons);
    this.reasons.clear();
    this.isFlushing = true;
    this.lastFlushStartedAt = this.now();

    try {
      await this.onFlush(pendingReasons);
    } finally {
      this.isFlushing = false;
      this.scheduleFlush();
    }
  }
}
